// /app/frontend/src/components/frugal-v2/components/CloseMonthModal.jsx
// Close month modal: 2-step confirmation + detailed summary + emphasis on preserved data

import React, { useState, useEffect } from 'react';
import { X, Calendar, AlertTriangle, CheckCircle, Trash2, RefreshCw, FileText, Shield, ChevronRight } from 'lucide-react';
import * as api from '../services/frugalApi';

const CloseMonthModal = ({ isOpen, onClose, token, t, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: Summary, 2: Final confirmation
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState(null);
  const [understood, setUnderstood] = useState(false);

  useEffect(() => {
    if (isOpen && token) {
      setStep(1);
      setUnderstood(false);
      fetchData();
    }
  }, [isOpen, token]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statusData, summaryData] = await Promise.all([
        api.fetchClosingStatus(token),
        api.fetchClosingSummary(token)
      ]);
      setStatus(statusData);
      setSummary(summaryData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    setExecuting(true);
    setError(null);
    try {
      const result = await api.executeClosing(token, true);
      onSuccess?.(result);
      onClose();
    } catch (err) {
      setError(err.message);
      setExecuting(false);
    }
  };

  if (!isOpen) return null;

  const monthName = new Date().toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  const nextMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-slate-900 rounded-2xl w-full max-w-lg max-h-[92vh] overflow-hidden border border-white/10 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-emerald-400" />
            <h2 className="text-lg font-semibold text-white capitalize">
              {step === 1 ? `Cerrar ${monthName}` : 'Confirmar cierre'}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg" data-testid="close-month-modal-x">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex gap-1 px-4 pt-3 shrink-0">
          <div className={`flex-1 h-1.5 rounded-full ${step >= 1 ? 'bg-emerald-500' : 'bg-white/10'}`} />
          <div className={`flex-1 h-1.5 rounded-full ${step >= 2 ? 'bg-emerald-500' : 'bg-white/10'}`} />
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {loading ? (
            <div className="text-center py-8 text-slate-400">Cargando resumen...</div>
          ) : error ? (
            <div className="text-center py-8 text-red-400">{error}</div>
          ) : status?.already_closed ? (
            <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg p-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
              <div className="text-amber-300 text-sm">Este mes ya fue cerrado.</div>
            </div>
          ) : step === 1 ? (
            <>
              {/* Pending emails warning */}
              {status?.pending_emails > 0 && (
                <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-amber-200 text-xs">
                    Tienes <strong>{status.pending_emails} emails pendientes</strong> de revisar. Si cierras ahora se borraran sin aprobar.
                  </div>
                </div>
              )}

              {/* Process overview - no numbers, only what the process does */}
              <div className="bg-gradient-to-br from-emerald-500/10 to-sky-500/10 border border-emerald-500/20 rounded-xl p-4">
                <h3 className="text-white font-medium mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-emerald-400" />
                  Qué incluye este proceso
                </h3>
                <ul className="text-sm text-slate-300 space-y-1.5 list-disc pl-5">
                  <li>Archiva un <strong>resumen permanente</strong> del mes actual en tu historial.</li>
                  <li>Reinicia los gastos recurrentes pagados para que vuelvan a estar pendientes el próximo mes.</li>
                  <li>Restablece la Reserva de Contingencia al máximo configurado.</li>
                  <li>Limpia los datos transitorios (emails procesados, transacciones y gastos no recurrentes) tras registrarlos en el resumen.</li>
                </ul>
              </div>

              {/* PRESERVED data */}
              <div className="bg-emerald-500/5 border-2 border-emerald-500/30 rounded-xl p-4">
                <h3 className="text-emerald-300 font-semibold mb-3 flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Lo que se CONSERVA
                </h3>
                <div className="space-y-2 text-sm text-slate-200">
                  <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /><div><strong>Resumen del mes</strong> — guardado permanentemente en tu historial</div></div>
                  <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /><div><strong>Ingresos y ahorros</strong> — siguen activos, con sus totales acumulados intactos</div></div>
                  <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /><div><strong>Reserva Vital</strong> — saldo y categorías se mantienen</div></div>
                  <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /><div><strong>Reserva Contingencia</strong> — vuelve al máximo configurado</div></div>
                  <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /><div><strong>Cuentas y saldos</strong> — bancos, tarjetas y balances</div></div>
                  <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /><div><strong>Categorías y presupuestos</strong> — listos para el nuevo mes</div></div>
                </div>
              </div>

              {/* RESET data */}
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
                <h3 className="text-amber-300 font-medium mb-3 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Lo que se REINICIA
                </h3>
                <div className="space-y-2 text-sm text-slate-300">
                  <div className="flex items-start gap-2"><RefreshCw className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" /><div>Los gastos recurrentes pagados vuelven a "Pendiente" para el próximo mes</div></div>
                  <div className="flex items-start gap-2"><RefreshCw className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" /><div>La Reserva de Contingencia se restablece al monto máximo</div></div>
                </div>
              </div>

              {/* DELETED data */}
              <div className="bg-rose-500/5 border border-rose-500/20 rounded-xl p-4">
                <h3 className="text-rose-300 font-medium mb-3 flex items-center gap-2">
                  <Trash2 className="w-4 h-4" />
                  Lo que se BORRA (ya contabilizado en el resumen)
                </h3>
                <div className="text-xs text-slate-400">
                  Emails procesados, transacciones del Daily Zen y gastos no recurrentes del mes. Los montos ya quedaron registrados en el resumen permanente.
                </div>
              </div>

              <p className="text-xs text-slate-400 text-center">Tu proximo cierre sera el <strong className="text-emerald-400 capitalize">{nextMonth}</strong></p>
            </>
          ) : (
            // Step 2: Final confirmation
            <>
              <div className="bg-rose-500/10 border-2 border-rose-500/30 rounded-xl p-5 text-center">
                <AlertTriangle className="w-12 h-12 text-rose-400 mx-auto mb-3" />
                <h3 className="text-white text-lg font-bold mb-2">¿Confirmas el cierre?</h3>
                <p className="text-sm text-slate-300 mb-4">
                  Esta accion <strong className="text-white">no se puede deshacer</strong>. El resumen del mes quedara guardado para siempre en tu historial.
                </p>
              </div>

              <div className="bg-white/5 rounded-xl p-4 space-y-2 text-sm">
                <div className="text-slate-400 mb-2">Recapitulando lo que <strong className="text-emerald-400">se conserva</strong>:</div>
                <ul className="space-y-1 text-slate-200 text-xs pl-2">
                  <li>✓ Resumen permanente en historial</li>
                  <li>✓ Cuentas, ingresos, ahorros, reservas, categorias</li>
                  <li>✓ Tu config completa lista para el proximo mes</li>
                </ul>
              </div>

              <label className="flex items-start gap-3 p-3 bg-white/5 rounded-xl cursor-pointer hover:bg-white/10">
                <input
                  type="checkbox"
                  checked={understood}
                  onChange={(e) => setUnderstood(e.target.checked)}
                  data-testid="close-month-understood-checkbox"
                  className="w-5 h-5 accent-emerald-500 mt-0.5"
                />
                <span className="text-sm text-slate-200">
                  Entiendo que el resumen se guarda y los datos transitorios (emails, transacciones, gastos no recurrentes) seran borrados despues de quedar registrados en el resumen.
                </span>
              </label>

              {error && <div className="text-rose-400 text-sm text-center">{error}</div>}
            </>
          )}
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-white/10 flex gap-3 shrink-0">
          {step === 1 ? (
            <>
              <button
                onClick={onClose}
                className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-slate-300 rounded-xl font-medium"
                data-testid="close-month-cancel-btn"
              >
                Cancelar
              </button>
              <button
                onClick={() => setStep(2)}
                disabled={loading || status?.already_closed || !!error}
                className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="close-month-continue-btn"
              >
                Continuar <ChevronRight className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setStep(1)}
                disabled={executing}
                className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-slate-300 rounded-xl font-medium disabled:opacity-50"
                data-testid="close-month-back-btn"
              >
                Volver
              </button>
              <button
                onClick={handleExecute}
                disabled={!understood || executing}
                className="flex-1 py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="close-month-execute-btn"
              >
                {executing ? 'Cerrando...' : 'Si, cerrar mes'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CloseMonthModal;
